/**
 * Spend Easy - Smart Budgeting Intelligence & Predictive Burn-Rate Engine
 * Analyzes spending pace, detects 80%/100% threshold crossings, and forecasts end-of-month totals.
 */

import {
  getDate,
  getDaysInMonth,
  startOfMonth,
  endOfMonth,
  isSameMonth,
} from 'date-fns';

export const BUDGET_STATUS = {
  HEALTHY: 'HEALTHY',       // < 75% and not projected to overspend
  MODERATE: 'MODERATE',     // 60-79%
  WARNING: 'WARNING',       // 80-99% or projected to exceed budget
  OVERBUDGET: 'OVERBUDGET', // >= 100%
};

/**
 * Calculates budget status, daily burn rates, and predictive forecasts
 */
export function calculateBudgetIntelligence(
  budgets = {},
  transactions = [],
  selectedMonth = new Date(),
  currencyFormatterOrSymbol = '$'
) {
  const fmt = typeof currencyFormatterOrSymbol === 'function'
    ? currencyFormatterOrSymbol
    : (val) => {
        const symbol = String(currencyFormatterOrSymbol || '$');
        const num = Number(val || 0);
        return `${symbol}${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      };

  const monthStart = startOfMonth(selectedMonth);
  const monthEnd = endOfMonth(selectedMonth);
  const isCurrentMonth = isSameMonth(selectedMonth, new Date());

  const daysInMonth = getDaysInMonth(selectedMonth);
  const currentDay = isCurrentMonth ? Math.max(1, getDate(new Date())) : daysInMonth;
  const daysRemaining = Math.max(0, daysInMonth - currentDay);
  const monthProgressPct = Math.round((currentDay / daysInMonth) * 100);

  // Filter expenses for selected month
  const monthlyExpenses = transactions.filter((t) => {
    if (t.type !== 'expense') return false;
    const d = t.date instanceof Date ? t.date : new Date(t.date);
    return d >= monthStart && d <= monthEnd;
  });

  // Category totals
  const spentByCategory = {};
  monthlyExpenses.forEach((t) => {
    const cat = t.category || 'Uncategorized';
    spentByCategory[cat] = (spentByCategory[cat] || 0) + (parseFloat(t.amount) || 0);
  });

  // All relevant categories (either has budget or has spend)
  const allCategoryNames = Array.from(
    new Set([...Object.keys(budgets || {}), ...Object.keys(spentByCategory)])
  );

  const categories = allCategoryNames.map((category) => {
    const budgetLimit = budgets[category] ? parseFloat(budgets[category]) : 0;
    const hasBudget = budgetLimit > 0;
    const spent = spentByCategory[category] || 0;
    const percentage = hasBudget ? (spent / budgetLimit) * 100 : 0;
    const remaining = hasBudget ? Math.max(0, budgetLimit - spent) : 0;
    const overspentAmount = hasBudget ? Math.max(0, spent - budgetLimit) : 0;

    // Daily pace & predictive projection
    const dailyBurnRate = currentDay > 0 ? spent / currentDay : 0;
    const projectedTotal = dailyBurnRate * daysInMonth;
    const projectedOverspend = hasBudget ? Math.max(0, projectedTotal - budgetLimit) : 0;
    const targetDailySpend = daysRemaining > 0 && remaining > 0 ? remaining / daysRemaining : 0;

    // Risk classification
    let status = BUDGET_STATUS.HEALTHY;
    let badgeColor = '#4CAF50';
    let statusTitle = 'On Track';
    let advice = `Pacing well. Target max ${fmt(targetDailySpend)}/day for remaining ${daysRemaining} days.`;

    if (hasBudget) {
      if (spent >= budgetLimit) {
        status = BUDGET_STATUS.OVERBUDGET;
        badgeColor = '#E8544F';
        statusTitle = 'Over Budget!';
        advice = `Exceeded limit by ${fmt(overspentAmount)}. Pause non-essential spending.`;
      } else if (percentage >= 80) {
        status = BUDGET_STATUS.WARNING;
        badgeColor = '#FF9800';
        statusTitle = 'Near Limit (80%+)';
        advice = `Only ${fmt(remaining)} left. Cap spending at ${fmt(targetDailySpend)}/day to stay within limit.`;
      } else if (projectedTotal > budgetLimit && currentDay >= 5) {
        status = BUDGET_STATUS.WARNING;
        badgeColor = '#FF9800';
        statusTitle = 'Predictive Overspend';
        advice = `At current pace (${fmt(dailyBurnRate)}/day), projected to exceed limit by ${fmt(projectedOverspend)}. Reduce daily spend to ${fmt(targetDailySpend)}/day.`;
      } else if (percentage >= 60) {
        status = BUDGET_STATUS.MODERATE;
        badgeColor = '#F59E0B';
        statusTitle = 'Moderate';
        advice = `${percentage.toFixed(0)}% used with ${daysRemaining} days left. Daily budget: ${fmt(targetDailySpend)}.`;
      }
    } else {
      statusTitle = 'No Budget';
      badgeColor = '#8E8E93';
      advice = 'Set a spending limit to enable automated warnings.';
    }

    return {
      category,
      budgetLimit,
      hasBudget,
      spent,
      percentage: Math.min(percentage, 999),
      remaining,
      overspentAmount,
      dailyBurnRate,
      projectedTotal,
      projectedOverspend,
      targetDailySpend,
      status,
      badgeColor,
      statusTitle,
      advice,
    };
  });

  // Sort: Overbudget first, then warnings, then highest percentage
  categories.sort((a, b) => {
    const priority = {
      [BUDGET_STATUS.OVERBUDGET]: 4,
      [BUDGET_STATUS.WARNING]: 3,
      [BUDGET_STATUS.MODERATE]: 2,
      [BUDGET_STATUS.HEALTHY]: 1,
    };
    const diff = (priority[b.status] || 0) - (priority[a.status] || 0);
    if (diff !== 0) return diff;
    return b.percentage - a.percentage;
  });

  // Overall statistics
  const budgetedCategories = categories.filter((c) => c.hasBudget);
  const totalBudget = budgetedCategories.reduce((sum, c) => sum + c.budgetLimit, 0);
  const totalSpent = budgetedCategories.reduce((sum, c) => sum + c.spent, 0);
  const totalProjected = budgetedCategories.reduce((sum, c) => sum + c.projectedTotal, 0);
  const overallPercentage = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;
  const overallRemaining = Math.max(0, totalBudget - totalSpent);
  const overallOverspend = Math.max(0, totalSpent - totalBudget);

  const activeAlerts = categories.filter(
    (c) => c.status === BUDGET_STATUS.OVERBUDGET || c.status === BUDGET_STATUS.WARNING
  );

  return {
    categories,
    budgetedCategoriesCount: budgetedCategories.length,
    totalBudget,
    totalSpent,
    totalProjected,
    overallPercentage: Math.min(overallPercentage, 999),
    overallRemaining,
    overallOverspend,
    currentDay,
    daysInMonth,
    daysRemaining,
    monthProgressPct,
    activeAlerts,
    hasCriticalAlerts: activeAlerts.some((a) => a.status === BUDGET_STATUS.OVERBUDGET),
  };
}
